import type React from 'react';

export enum AuthType {
  OAUTH = 'oauth',
  API_KEY = 'api_key',
  OAUTH_WITH_CREDENTIALS = 'oauth_with_credentials',
  // Multi-field credentials validated via a side-effect-free lookup call
  // (which returns provider data to populate a second step) before the
  // real connect call — no OAuth leg. See SpotDraftConnectDrawer.
  CREDENTIALS_WITH_LOOKUP = 'credentials_with_lookup',
}

export interface OAuthConfig {
  formMethod: string;
  formUrl: string;
}

export interface ApiKeyConfig {
  endpoint: string;
  fieldLabel: string;
  fieldPlaceholder: string;
}

export interface CredentialField {
  name: string;
  label: string;
  placeholder: string;
  validationSchema: any;
}

export interface CredentialsConfig {
  endpoint: string;
  fields: CredentialField[];
  oauthConfig: OAuthConfig;
}

export interface ProviderConfig {
  id: string;
  label: string;
  img?: string;
  description: string;
  descriptionForm?: string;
  note?: string;
  icon?: React.ComponentType;
  iconProps?: Record<string, any>;
  authType: AuthType;
  integrationType: string;
  oauthConfig?: OAuthConfig;
  apiKeyConfig?: ApiKeyConfig;
  credentialsConfig?: CredentialsConfig;
}

export interface ESignatureConnectionProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  providerId: string;
  onTriggerOAuth?: () => void;
}

export interface ConnectApiKeyPayload {
  integration_type: string;
  api_key: string;
}

export interface ConnectCredentialsPayload {
  integration_type: string;
  client_id: string;
  client_secret: string;
}

export interface ConnectApiKeyRequest {
  account_integration: ConnectApiKeyPayload;
}

export interface ConnectCredentialsRequest {
  account_integration: ConnectCredentialsPayload;
}

export interface ApiResponse {
  success?: boolean;
  message?: string;
  error?: string;
}

export interface FormField {
  name: string;
  label: string;
  placeholder: string;
  required: boolean;
  validationSchema: any;
  type: string;
  customOnChange: (e: any) => void;
}

export type ESignatureProvider =
  | 'docusign'
  | 'pandadocs'
  | 'adobe'
  | 'spotdraft';

export interface IntegrationData {
  id?: string;
  integration_type: string;
  [key: string]: any;
}

export interface SelectedIntegration {
  mode?: string;
  data: IntegrationData;
  cardConfig: Partial<ProviderConfig>;
}

export interface CardConfig {
  id: string;
  label: string;
  subLabel: string;
  img?: string;
  icon?: React.ComponentType;
  iconProps?: Record<string, any>;
  isConnected: boolean;
  isLoading: boolean;
  // Omitted for providers with no edit flow (e.g. SpotDraft, whose edit
  // endpoint 404s) — SettingsToggleCard hides the pencil when unset.
  onEdit?: () => void;
  onToggle: () => void;
}

export interface EsignEditProps {
  modalActions: {
    isOpen: boolean;
    onClose: () => void;
    onOpen: () => void;
  };
  isAdmin?: boolean;
  offerId?: string;
  onSuccess?: () => void;
  integrationType?: string;
  jobId?: string;
  // When true (DocuSign only), the drawer's single CTA becomes "Preview on Docusign" — it creates a
  // draft envelope and redirects the current tab to the embedded sender view (same-tab, to dodge
  // popup blockers) instead of sending immediately.
  enablePreview?: boolean;
}

export interface FormValues {
  subject?: string;
  body?: string;
  company_signer_required?: string;
  signing_order?: string;
  company_signer_id?: string;
}

export interface UpdateEsignPayload {
  company_signer_required: boolean;
  signing_order: 'company_signer_first' | 'candidate_first';
  email_subject: string;
  email_body: string;
  company_signer_id?: string;
}

export interface SendEsignPayload {
  esign_envelope: {
    email_subject: string;
    email_body: string;
    company_signer_id?: string;
  };
}

export type SpotDraftRegion = 'us' | 'in' | 'eu' | 'me';

export interface SpotDraftContractType {
  id: number;
  name: string;
}

export interface SpotDraftContractTypesPayload {
  client_id: string;
  client_secret: string;
  region: SpotDraftRegion;
}

export interface SpotDraftContractTypesResponse {
  contract_types: SpotDraftContractType[];
}

export interface SpotDraftConnectPayload {
  integration_type: string;
  api_key: string;
  spotdraft_client_id: string;
  spotdraft_region: SpotDraftRegion;
  spotdraft_contract_type_id: number;
}
